// src/hooks/useSocket.js
import { useEffect, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import { getApiOrigin } from "../services/apiBase";

const SOCKET_URL = getApiOrigin();

let socketInstance = null;

function getSocket() {
  if (!socketInstance) {
    socketInstance = io(SOCKET_URL, {
      transports: ["websocket","polling"],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
    });
  }
  return socketInstance;
}

/**
 * Hook para gerenciar conexão WebSocket com Socket.io
 * @param {string|null} idCondominio - ID do condomínio para entrar na sala
 * @param {Object} handlers - { novo_comunicado: fn, ... }
 */
export default function useSocket(idCondominio, handlers = {}, options = {}) {
  const socket = useRef(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  // options: { condoIds: [], portalCpf: "" }

  useEffect(() => {
    socket.current = getSocket();

    // Single condo room
    if (idCondominio) {
      socket.current.emit("entrar_condo", idCondominio);
    }

    // Multiple condo rooms (portal do condômino)
    if (options.condoIds?.length) {
      socket.current.emit("entrar_condos", options.condoIds);
    }

    // Portal room by CPF
    if (options.portalCpf) {
      socket.current.emit("entrar_portal", options.portalCpf);
    }

    // Registra listeners
    const eventNames = Object.keys(handlersRef.current);
    eventNames.forEach(event => {
      socket.current.on(event, (...args) => {
        handlersRef.current[event]?.(...args);
      });
    });

    return () => {
      eventNames.forEach(event => socket.current?.off(event));
      if (idCondominio) socket.current?.emit("sair_condo", idCondominio);
      if (options.condoIds?.length) {
        options.condoIds.forEach(id => socket.current?.emit("sair_condo", id));
      }
      if (options.portalCpf) socket.current?.emit("sair_portal", options.portalCpf);
    };
  // eslint-disable-next-line
  }, [idCondominio]);

  const emit = useCallback((event, data) => {
    socket.current?.emit(event, data);
  }, []);

  return { emit };
}
